/**
 * Generates a callback to be called by useEffect when the component initially loads
 *
 * @param {object} params - Parameters
 * @param {function} params.getPerformanceMetrics - a callback to request performance metrics
 * @param {function} params.getRankProgression - a callback to request progression data
 * @param {boolean} params.hasPerformanceMetrics - whether there is currently performance metrics
 * @param {boolean} params.hasProgressionData - whether there is currently progression data
 *
 * @returns {function} - Callback - A function to be called by useEffect that requests updated performance metrics
 */
interface PerformanceOnMountParams {
  getPerformanceMetrics: () => void;
  getRankProgression: () => void;
  hasPerformanceMetrics: boolean;
  hasProgressionData: boolean;
}

export function generatePerformanceOnMount({
  getPerformanceMetrics,
  getRankProgression,
  hasPerformanceMetrics,
  hasProgressionData,
}: PerformanceOnMountParams) {
  return () => {
    if (!hasProgressionData) {
      getRankProgression();
    }
    if (!hasPerformanceMetrics) {
      // Wait for any potential screen transition/animation is done before ...
      setTimeout(() => {
        // ... fetching performance data from the API.
        getPerformanceMetrics();
      }, 500);
    }
  };
}

/**
 * Generates a callback to be called by useEffect that requests updated performance metrics
 *
 * @param {object} params - Parameters
 * @param {boolean} params.currentHasPerformanceMetrics - whether there is currently performance metrics
 * @param {function} params.getPerformanceMetrics - a callback to request performance metrics
 * @param {boolean} params.previousHasPerformanceMetrics - whether there was previously performance metrics
 *
 * @returns {function} - Callback - A function to be called by useEffect that requests updated performance metrics
 */

interface UpdatePerformanceMetricsParams {
  currentHasPerformanceMetrics: boolean;
  getPerformanceMetrics: () => void;
  previousHasPerformanceMetrics: boolean;
}

export function generateUpdatePerformanceMetrics({
  currentHasPerformanceMetrics,
  getPerformanceMetrics,
  previousHasPerformanceMetrics,
}: UpdatePerformanceMetricsParams) {
  return () => {
    if (previousHasPerformanceMetrics && !currentHasPerformanceMetrics) {
      getPerformanceMetrics();
    }
  };
}

/**
 * Generates a callback to be called by useEffect that requests updated progression data
 *
 * @param {object} params - Parameters
 * @param {boolean} params.currentHasProgressionData - whether there is currently progression data
 * @param {function} params.getRankProgression - a callback to request progression data
 * @param {boolean} params.previousHasProgressionData - whether there was previously progression data
 *
 * @returns {function} - Callback - A function to be called by useEffect that requests updated progression data
 */

interface UpdateProgressionDataParams {
  currentHasProgressionData: boolean;
  getRankProgression: () => void;
  previousHasProgressionData: boolean;
}
export function generateUpdateProgressionData({
  currentHasProgressionData,
  getRankProgression,
  previousHasProgressionData,
}: UpdateProgressionDataParams) {
  return () => {
    if (previousHasProgressionData && !currentHasProgressionData) {
      getRankProgression();
    }
  };
}
